import { IAppState } from '../core/redux/IAppState';
import { hasConfigLoadFailed, isConfigLoading } from '../auth/config/getConfigAction';
import {
  getResortMappingLoadError,
  haveResortMappingsLoaded,
  isResortMappingLoading,
  isResortMappingSuppressed,
} from './getResortMappingAction';
import {
  getResortMappingErrorHeldUntil,
  getResortMappingErrorLoadError,
  haveResortMappingErrorsLoaded,
  isResortMappingErrorLoading,
  isResortMappingErrorSuppressed,
} from './getResortMappingErrorAction';
import {
  getProductHydrationErrorsHeldUntil,
  getProductHydrationErrorsLoadError,
  haveProductHydrationErrorsLoaded,
  isProductHydrationErrorsLoading,
  isProductHydrationErrorsSuppressed,
} from './getProductHydrationErrorsAction';


/**
 * Where one of the Product Mapping page's loads stands for the selected resort. Keyed on what has landed for that
 * resort rather than on a request in flight: a suppressed dispatch never goes out, and a request registers a
 * microtask after dispatch.
 */
export type LoadStatus =
  /** This user or config never makes the load. */
  | 'notExpected'
  /** Nothing for this resort yet: in flight, or expected and not started. */
  | 'loading'
  /** Data for this resort on screen, a new request in flight. */
  | 'refreshing'
  | 'loaded'
  /** Nothing for this resort: the last attempt failed, or the breaker would suppress the next. */
  | 'failed'
  /** Data for this resort on screen, the latest refresh failed. */
  | 'refreshFailed';


export type ProductMappingLoadStatus = {
  rows: LoadStatus;
  mappingStatus: LoadStatus;
  hydrationErrors: LoadStatus;
};


export const isBusy = (status: LoadStatus): boolean => status === 'loading' || status === 'refreshing';


export const hasFailed = (status: LoadStatus): boolean => status === 'failed' || status === 'refreshFailed';


/** What a check column's cells show while the check has nothing to show; undefined once it has. */
export const cellPlaceholder = (status: LoadStatus): { text: string, titleKey: string } | undefined => {
  if (status === 'loading') return { text: '…', titleKey: 'PRODUCT_MAPPING_PAGE_CELL_LOADING' };
  if (status === 'failed') return { text: '—', titleKey: 'PRODUCT_MAPPING_PAGE_CELL_UNAVAILABLE' };
  return undefined;
};


/** Only these roles see the Hydration Errors column, so only they load it. */
export const showsHydrationErrors = (role?: string): boolean => role === 'sysadmin' || role === 'admin';


const hydrationErrorsExpected = (state: IAppState): boolean =>
  showsHydrationErrors(state.userState?.role ?? 'agent') && state.resortToolsConfig.enableResortHydrationUIFeatures;


/** resortToolsConfig lags a resort switch, and its flags decide whether to load at all. */
export const isConfigForSelectedResort = (state: IAppState): boolean =>
  parseInt(state.resortToolsConfig.resortId) === state.selectedResort.resortId;


type LoadInputs = {
  inFlight: boolean;
  landed: boolean;
  failed: boolean;
  suppressed: boolean;
};


// First match wins
const statusOf = ({ inFlight, landed, failed, suppressed }: LoadInputs): LoadStatus => {
  if (inFlight) return landed ? 'refreshing' : 'loading';
  if (failed) return landed ? 'refreshFailed' : 'failed';
  if (landed) return 'loaded';
  if (suppressed) return 'failed';
  return 'loading';
};


/**
 * Rows and Mapping Status wait for the selected resort's config, whose flags may not be read until it lands. A
 * failed config load installs the default config, which matches no resort, so it has to read as failed.
 */
const configWait = (state: IAppState): LoadStatus | undefined => {
  if (hasConfigLoadFailed(state) && !isConfigLoading(state)) return 'failed';
  return isConfigForSelectedResort(state) ? undefined : 'loading';
};


export const getProductMappingLoadStatus = (state: IAppState): ProductMappingLoadStatus => {
  const resort = state.selectedResort.resortAbbreviation;
  const config = state.resortToolsConfig;
  const role = state.userState?.role ?? 'agent';
  const waitingOnConfig = configWait(state);
  // Same gate ResortSelectMenu loads both by
  const mappingsExpected = config.enableProductMapping.includes(role) && config.enableResortHydrationUIFeatures;

  const forMappings = (load: () => LoadInputs): LoadStatus =>
    waitingOnConfig ?? (mappingsExpected ? statusOf(load()) : 'notExpected');

  return {
    rows: forMappings(() => ({
      inFlight:   isResortMappingLoading(state, resort),
      // Either response writes rows
      landed:     haveResortMappingsLoaded(state, resort) || haveResortMappingErrorsLoaded(state, resort),
      failed:     !!getResortMappingLoadError(state, resort),
      suppressed: isResortMappingSuppressed(state),
    })),
    mappingStatus: forMappings(() => ({
      inFlight:   isResortMappingErrorLoading(state, resort),
      landed:     haveResortMappingErrorsLoaded(state, resort),
      failed:     !!getResortMappingErrorLoadError(state, resort),
      suppressed: isResortMappingErrorSuppressed(state),
    })),
    // Keyed by resort alone, so it does not wait for the config
    hydrationErrors: hydrationErrorsExpected(state)
      ? statusOf({
        inFlight:   isProductHydrationErrorsLoading(state, resort),
        landed:     haveProductHydrationErrorsLoaded(state, resort),
        failed:     !!getProductHydrationErrorsLoadError(state, resort),
        suppressed: isProductHydrationErrorsSuppressed(state),
      })
      : 'notExpected',
  };
};


/**
 * The last moment a breaker holds one of Sync's requests, once one has tripped: a Sync until then would wait on a load
 * that never goes out. In the past once the hold ends, so compare it with the clock.
 */
export const getSyncHeldUntil = (state: IAppState): number | undefined => {
  const times = [
    getResortMappingErrorHeldUntil(state),
    hydrationErrorsExpected(state) ? getProductHydrationErrorsHeldUntil(state) : undefined,
  ].filter((time): time is number => time !== undefined);
  return times.length > 0 ? Math.max(...times) : undefined;
};


const MINUTE_MS = 60 * 1000;

/**
 * When to say Sync is back. The time format stops at minutes, so round up: back before the time shown is fine, after
 * it is not.
 */
export const shownSyncTime = (heldUntil: number): Date => new Date(Math.ceil((heldUntil + 1) / MINUTE_MS) * MINUTE_MS);
